/**
 * ClickReel SDK - YouTube Player Overlay
 *
 * Full-screen modal on mobile, PiP-style on desktop
 * Uses YouTube IFrame Player API for video playback
 */

import {
  VideoProgressData,
  BubblePosition,
  StylingConfig,
  FloatingAction,
  ProductCardConfig,
  SocialProofConfig,
} from './types';
import { getShadowContainer } from './shadow-dom';
import { ProductCard } from './product-card';
import { createUnmuteOverlay, getConnectionQuality, injectUtilityStyles, isMobile, loadYouTubeApi } from './video-utils';
import { buildCtaUrl } from './analytics-bridge';

// =============================================================================
// YouTube IFrame API Types
// =============================================================================

declare global {
  interface Window {
    YT: typeof YT;
    onYouTubeIframeAPIReady: () => void;
  }
}

declare namespace YT {
  class Player {
    constructor(elementId: string | HTMLElement, options: PlayerOptions);
    playVideo(): void;
    pauseVideo(): void;
    stopVideo(): void;
    seekTo(seconds: number, allowSeekAhead: boolean): void;
    mute(): void;
    unMute(): void;
    isMuted(): boolean;
    setVolume(volume: number): void;
    getVolume(): number;
    getCurrentTime(): number;
    getDuration(): number;
    getPlayerState(): PlayerState;
    destroy(): void;
  }

  interface PlayerOptions {
    videoId?: string;
    width?: number | string;
    height?: number | string;
    playerVars?: PlayerVars;
    events?: PlayerEvents;
  }

  interface PlayerVars {
    autoplay?: 0 | 1;
    controls?: 0 | 1;
    disablekb?: 0 | 1;
    enablejsapi?: 0 | 1;
    fs?: 0 | 1;
    iv_load_policy?: 1 | 3;
    loop?: 0 | 1;
    modestbranding?: 0 | 1;
    origin?: string;
    playsinline?: 0 | 1;
    rel?: 0 | 1;
    showinfo?: 0 | 1;
    mute?: 0 | 1;
  }

  interface PlayerEvents {
    onReady?: (event: PlayerEvent) => void;
    onStateChange?: (event: OnStateChangeEvent) => void;
    onError?: (event: OnErrorEvent) => void;
  }

  interface PlayerEvent {
    target: Player;
  }

  interface OnStateChangeEvent {
    target: Player;
    data: PlayerState;
  }

  interface OnErrorEvent {
    target: Player;
    data: number;
  }

  enum PlayerState {
    UNSTARTED = -1,
    ENDED = 0,
    PLAYING = 1,
    PAUSED = 2,
    BUFFERING = 3,
    CUED = 5,
  }
}

// =============================================================================
// YouTube Player Component
// =============================================================================

export interface YouTubePlayerOptions {
  videoId: string;
  position: BubblePosition;
  styling: StylingConfig;
  onPlay?: () => void;
  onPause?: () => void;
  onProgress?: (data: VideoProgressData) => void;
  onComplete?: () => void;
  onClose?: () => void;
  onCtaClick?: () => void;
  onFloatingActionClick?: (action: FloatingAction) => void;
  ctaText?: string;
  ctaUrl?: string;
  floatingActions?: FloatingAction[];
  muteOnOpen?: boolean;
  productCard?: ProductCardConfig;
  socialProof?: SocialProofConfig;
  storyMode?: boolean;
  storyIndex?: number;
  storyTotal?: number;
  onStoryAdvance?: (direction: 'next' | 'prev') => void;
  showBranding?: boolean;
  campaignName?: string;
  sessionId?: string;
  videoIdForTracking?: string;
}

/**
 * YouTube player overlay component using IFrame Player API
 */
export class YouTubePlayer {
  private overlay: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private playerContainer: HTMLElement | null = null;
  private player: YT.Player | null = null;
  private options: YouTubePlayerOptions;
  private isOpen = false;
  private lastQuartile = 0;
  private progressInterval: number | null = null;
  private apiLoaded = false;
  private apiLoadPromise: Promise<void> | null = null;
  private productCardInstance: ProductCard | null = null;
  private playerReady = false;
  private isMutedState = true;
  private closing = false;
  private destroyed = false;
  private autoplayFallbackTimer: number | null = null;
  private storyNavLeft: HTMLElement | null = null;
  private storyNavRight: HTMLElement | null = null;

  constructor(options: YouTubePlayerOptions) {
    this.options = options;
  }

  /**
   * Open the video player
   */
  async open(): Promise<void> {
    if (this.isOpen) return;

    const shadow = getShadowContainer();
    if (!shadow) {
      throw new Error('Shadow DOM not initialized');
    }

    // Ensure YouTube API is loaded
    await this.loadYouTubeAPI();

    this.render(shadow);
    this.isOpen = true;

    // Create player immediately (preserves user gesture context for mobile autoplay)
    this.createPlayer();
  }

  /**
   * Close the video player
   */
  close(): void {
    if (!this.isOpen || !this.overlay) return;
    this.closing = true;

    this.stopProgressTracking();

    // Animate out
    this.overlay.classList.add('closing');

    setTimeout(() => {
      this.destroy();
      this.options.onClose?.();
    }, 200);
  }

  /**
   * Toggle play/pause
   */
  togglePlay(): void {
    if (!this.player) return;

    const state = this.player.getPlayerState();
    if (state === YT.PlayerState.PLAYING) {
      this.player.pauseVideo();
    } else {
      this.player.playVideo();
    }
  }

  /**
   * Check if player is currently open
   */
  get isVisible(): boolean {
    return this.isOpen;
  }

  /**
   * Destroy the player and clean up
   */
  destroy(): void {
    this.destroyed = true; // Set BEFORE player.destroy() to block async events
    this.closing = true; // Also ensure closing is true during teardown
    this.stopProgressTracking();
    if (this.autoplayFallbackTimer !== null) {
      clearTimeout(this.autoplayFallbackTimer);
      this.autoplayFallbackTimer = null;
    }
    this.productCardInstance?.destroy();
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
      this.container = null;
      this.playerContainer = null;
    }
    this.isOpen = false;
    this.lastQuartile = 0;
    this.playerReady = false;
    this.isMutedState = true;
    this.storyNavLeft = null;
    this.storyNavRight = null;

    // Remove escape key handler
    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  // ===========================================================================
  // Private Methods - YouTube API
  // ===========================================================================

  private loadYouTubeAPI(): Promise<void> {
    if (this.apiLoaded) {
      return Promise.resolve();
    }

    if (this.apiLoadPromise) {
      return this.apiLoadPromise;
    }

    this.apiLoadPromise = loadYouTubeApi().then(() => {
      this.apiLoaded = true;
    });

    return this.apiLoadPromise;
  }

  private createPlayer(): void {
    if (!this.playerContainer) return;

    // Create a div inside playerContainer for the iframe
    const playerDiv = document.createElement('div');
    playerDiv.id = 'clickreel-yt-player-' + Date.now();
    this.playerContainer.appendChild(playerDiv);

    // Adaptive quality: skip autoplay on slow connections
    const quality = getConnectionQuality();
    const autoplay: 0 | 1 = quality === 'slow' ? 0 : 1;

    this.player = new YT.Player(playerDiv, {
      videoId: this.options.videoId,
      width: '100%',
      height: '100%',
      playerVars: {
        autoplay,
        controls: 0,
        disablekb: 0,
        enablejsapi: 1,
        fs: 0,
        iv_load_policy: 3, // Hide annotations
        modestbranding: 1,
        playsinline: 1,
        rel: 0, // Don't show related videos
        mute: (this.options.muteOnOpen || isMobile()) ? 1 : 0, // Mobile: always mute for guaranteed autoplay
        origin: window.location.origin !== 'null' ? window.location.origin : (document.referrer ? new URL(document.referrer).origin : '*'), // srcdoc iframes have origin "null" — use parent origin
      },
      events: {
        onReady: (event) => this.onPlayerReady(event),
        onStateChange: (event) => this.onPlayerStateChange(event),
        onError: (event) => this.onPlayerError(event),
      },
    });
  }

  private onPlayerReady(_event: YT.PlayerEvent): void {
    this.playerReady = true;
    // Sync muted state from the actual player now that it's ready
    this.isMutedState = this.isPlayerMuted();

    // Start progress tracking
    this.startProgressTracking();

    // Emit play event
    this.options.onPlay?.();

    // On mobile we started muted (playerVars mute:1) to guarantee autoplay.
    // Show the unmute overlay immediately so user can tap to hear audio.
    if (isMobile() && !this.options.muteOnOpen) {
      this.isMutedState = true;
      this.updateMuteButton(true);
      // Append to videoWrapper (playerContainer's parent) so the overlay is in the same
      // stacking context as story nav zones — z-index 25 beats nav's 18.
      const overlayParent = this.playerContainer?.parentElement || this.container!;
      createUnmuteOverlay(() => {
        this.player?.unMute();
        this.player?.setVolume(100);
        this.isMutedState = false;
        this.updateMuteButton(false);
      }, overlayParent);
    }

    // Fallback: if autoplay still failed (e.g. slow connection skip, or unexpected block),
    // try muted playback after a short delay.
    this.autoplayFallbackTimer = window.setTimeout(() => {
      this.autoplayFallbackTimer = null;
      if (!this.player || this.destroyed || this.closing) return;
      const state = this.player.getPlayerState();
      if (state !== YT.PlayerState.PLAYING && state !== YT.PlayerState.BUFFERING) {
        // Autoplay was blocked even with mute — try programmatic play
        this.player.mute();
        this.isMutedState = true;
        this.updateMuteButton(true);
        this.player.playVideo();
      }
    }, 500);
  }

  private onPlayerStateChange(event: YT.OnStateChangeEvent): void {
    // Sprint 003 (2026-04-29): hard top-of-function guard. Previously this
    // only short-circuited the ENDED-in-story-mode path; late PLAYING/PAUSED/
    // ENDED events from the IFrame API still fired onPlay/onPause/onComplete
    // callbacks against a torn-down player, producing phantom video_play and
    // video_complete tracking events.
    if (this.destroyed || this.closing) return;

    switch (event.data) {
      case YT.PlayerState.PLAYING:
        // Cancel autoplay fallback — autoplay succeeded on its own
        if (this.autoplayFallbackTimer !== null) {
          clearTimeout(this.autoplayFallbackTimer);
          this.autoplayFallbackTimer = null;
        }
        this.options.onPlay?.();
        this.updatePlayButton(true);
        this.startProgressTracking();
        // Re-enable story nav zones now that video is playing
        if (this.storyNavLeft) this.storyNavLeft.style.pointerEvents = '';
        if (this.storyNavRight) this.storyNavRight.style.pointerEvents = '';
        break;
      case YT.PlayerState.PAUSED:
        this.options.onPause?.();
        this.updatePlayButton(false);
        // On mobile + story mode: disable nav zones so user can tap YouTube play button to resume
        if (isMobile() && this.options.storyMode) {
          if (this.storyNavLeft) this.storyNavLeft.style.pointerEvents = 'none';
          if (this.storyNavRight) this.storyNavRight.style.pointerEvents = 'none';
        }
        break;
      case YT.PlayerState.ENDED:
        this.options.onComplete?.();
        this.updatePlayButton(false);
        this.stopProgressTracking();
        this.emitProgress(100);
        // Story mode: auto-advance to next video (only if not closing/destroyed)
        if (this.options.storyMode && !this.closing && !this.destroyed) {
          this.options.onStoryAdvance?.('next');
        }
        break;
    }
  }

  private onPlayerError(_event: YT.OnErrorEvent): void {
    console.error('YouTube player error');
  }

  // ===========================================================================
  // Private Methods - Rendering
  // ===========================================================================

  private render(shadow: ShadowRoot): void {
    const mobile = isMobile();

    // Create overlay backdrop
    this.overlay = document.createElement('div');
    this.overlay.className = 'clickreel-video-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'Video player');

    // Click outside to close (on backdrop)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Create container
    this.container = document.createElement('div');
    this.container.className = `clickreel-video-container ${mobile ? 'mobile' : 'desktop'}`;

    // Apply position-based styles for desktop PiP
    if (!mobile) {
      this.applyPiPStyles();
    }

    // Build player content
    this.container.appendChild(this.createCloseButton());

    // Create video container with floating actions overlay
    const videoWrapper = this.createVideoContainer();

    // Add floating actions INSIDE the video wrapper so they overlay the video
    if (this.options.floatingActions && this.options.floatingActions.length > 0) {
      videoWrapper.appendChild(this.createFloatingActions());
    }

    // Stories mode UI
    this.createStoryUI(videoWrapper);

    // Product card slide-up
    if (this.options.productCard?.enabled) {
      this.productCardInstance = new ProductCard(this.options.productCard, this.options.styling.primaryColor);
      this.productCardInstance.render(videoWrapper);
    }

    // Social proof badge
    this.createSocialProof(videoWrapper);

    this.container.appendChild(videoWrapper);

    // Only show custom controls on desktop (mobile uses YouTube's native controls for cleaner UX)
    if (!mobile) {
      this.container.appendChild(this.createVideoControls());
    }

    if (this.options.showBranding) {
      const watermark = document.createElement('a');
      watermark.href = 'https://clickreel.io?utm_source=widget&utm_medium=referral&utm_campaign=powered_by';
      watermark.target = '_blank';
      watermark.rel = 'noopener';
      watermark.textContent = 'Powered by ClickReel.io';
      watermark.className = 'clickreel-player-watermark';
      videoWrapper.appendChild(watermark);
    }

    // Only show CTA on desktop if provided (mobile keeps users on site)
    if (!mobile && this.options.ctaText && this.options.ctaUrl) {
      this.container.appendChild(this.createCTAButton());
    }

    this.overlay.appendChild(this.container);

    // Inject styles
    this.injectStyles(shadow);

    shadow.appendChild(this.overlay);

    // Handle escape key
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  private createCloseButton(): HTMLElement {
    const button = document.createElement('button');
    button.className = 'clickreel-close-btn';
    button.setAttribute('aria-label', 'Close video');
    button.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.3 5.71a.996.996 0 00-1.41 0L12 10.59 7.11 5.7A.996.996 0 105.7 7.11L10.59 12 5.7 16.89a.996.996 0 101.41 1.41L12 13.41l4.89 4.89a.996.996 0 101.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z"/>
      </svg>
    `;

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    return button;
  }

  private createVideoContainer(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'clickreel-video-wrapper';

    // Portrait orientation for mobile-first
    wrapper.setAttribute('data-orientation', 'portrait');

    // Create container for YouTube player
    this.playerContainer = document.createElement('div');
    this.playerContainer.className = 'clickreel-youtube-container';

    wrapper.appendChild(this.playerContainer);

    return wrapper;
  }

  private createVideoControls(): HTMLElement {
    const controls = document.createElement('div');
    controls.className = 'clickreel-video-controls clickreel-yt-controls';

    // Play/Pause button
    const playBtn = document.createElement('button');
    playBtn.className = 'clickreel-play-btn';
    playBtn.innerHTML = this.getPauseIcon(); // Start with pause since autoplay
    playBtn.addEventListener('click', () => this.togglePlay());

    // Progress bar
    const progressContainer = document.createElement('div');
    progressContainer.className = 'clickreel-progress-container';

    const progressBar = document.createElement('div');
    progressBar.className = 'clickreel-progress-bar';

    const progressFill = document.createElement('div');
    progressFill.className = 'clickreel-progress-fill';

    progressBar.appendChild(progressFill);
    progressContainer.appendChild(progressBar);

    // Click on progress bar to seek
    progressContainer.addEventListener('click', (e) => {
      if (!this.player) return;
      const rect = progressBar.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const duration = this.player.getDuration();
      this.player.seekTo(percent * duration, true);
    });

    // Time display
    const timeDisplay = document.createElement('span');
    timeDisplay.className = 'clickreel-time-display';
    timeDisplay.textContent = '0:00 / 0:00';

    // Volume/Mute button
    const muteBtn = document.createElement('button');
    muteBtn.className = 'clickreel-mute-btn';
    muteBtn.innerHTML = this.getMuteIcon(); // Start muted
    muteBtn.addEventListener('click', () => {
      if (!this.player || !this.playerReady) return;
      if (this.isMutedState) {
        this.player.unMute();
        this.player.setVolume(100);
        this.isMutedState = false;
        muteBtn.innerHTML = this.getVolumeIcon();
      } else {
        this.player.mute();
        this.isMutedState = true;
        muteBtn.innerHTML = this.getMuteIcon();
      }
    });

    controls.appendChild(playBtn);
    controls.appendChild(progressContainer);
    controls.appendChild(timeDisplay);
    controls.appendChild(muteBtn);

    return controls;
  }

  private createCTAButton(): HTMLElement {
    const { styling } = this.options;

    const cta = document.createElement('a');
    cta.className = 'clickreel-cta-btn';
    cta.href = buildCtaUrl(this.options.ctaUrl, {
      campaignName: this.options.campaignName,
      videoId: this.options.videoIdForTracking,
      sessionId: this.options.sessionId,
    });
    cta.target = '_blank';
    cta.rel = 'noopener noreferrer';
    cta.textContent = this.options.ctaText || 'Learn More';
    cta.style.backgroundColor = styling.primaryColor || '#6366f1';

    cta.addEventListener('click', () => {
      this.options.onCtaClick?.();
    });

    return cta;
  }

  private createFloatingActions(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'clickreel-floating-actions';

    this.options.floatingActions?.forEach((action) => {
      const actionBtn = document.createElement('a');
      actionBtn.className = 'clickreel-floating-action';
      actionBtn.href = action.url;
      actionBtn.target = '_blank';
      actionBtn.rel = 'noopener noreferrer';

      // Left side: Shopping bag icon with pulse animation
      const iconWrapper = document.createElement('div');
      iconWrapper.className = 'clickreel-floating-action-icon-wrap';

      if (action.imageUrl) {
        const img = document.createElement('img');
        img.src = action.imageUrl;
        img.alt = action.label;
        img.className = 'clickreel-floating-action-img';
        iconWrapper.appendChild(img);
      } else {
        const icon = document.createElement('div');
        icon.className = 'clickreel-floating-action-icon';
        icon.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 7h-3V6a4 4 0 0 0-8 0v1H5a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zm-9-1a2 2 0 0 1 4 0v1h-4V6zm8 13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h4v1a1 1 0 0 0 2 0V9h2v10z"/>
          </svg>
        `;
        iconWrapper.appendChild(icon);
      }
      actionBtn.appendChild(iconWrapper);

      // Center: Product info
      const textContainer = document.createElement('div');
      textContainer.className = 'clickreel-floating-action-text';

      const label = document.createElement('span');
      label.className = 'clickreel-floating-action-label';
      label.textContent = action.label;
      textContainer.appendChild(label);

      if (action.subtitle) {
        const subtitle = document.createElement('span');
        subtitle.className = 'clickreel-floating-action-subtitle';
        subtitle.textContent = action.subtitle;
        textContainer.appendChild(subtitle);
      }

      actionBtn.appendChild(textContainer);

      // Right side: Shop Now button
      const shopBtn = document.createElement('span');
      shopBtn.className = 'clickreel-floating-action-btn';
      shopBtn.textContent = 'Shop';
      actionBtn.appendChild(shopBtn);

      // Variant picker (if action has variants)
      this.createVariantPicker(action, actionBtn);

      actionBtn.addEventListener('click', () => {
        this.options.onFloatingActionClick?.(action);
      });

      container.appendChild(actionBtn);
    });

    return container;
  }

  private createStoryUI(wrapper: HTMLElement): void {
    const { storyMode, storyIndex = 0, storyTotal = 1 } = this.options;
    if (!storyMode || storyTotal <= 1) return;

    // Progress bar
    const bar = document.createElement('div');
    bar.className = 'clickreel-story-bar';
    for (let i = 0; i < storyTotal; i++) {
      const seg = document.createElement('div');
      seg.className = 'clickreel-story-segment';
      if (i < storyIndex) {
        seg.classList.add('completed');
      } else if (i === storyIndex) {
        seg.classList.add('active');
        const fill = document.createElement('div');
        fill.className = 'clickreel-story-fill';
        seg.appendChild(fill);
      }
      bar.appendChild(seg);
    }
    wrapper.appendChild(bar);

    // Counter
    const counter = document.createElement('div');
    counter.className = 'clickreel-story-counter';
    counter.textContent = `${storyIndex + 1} / ${storyTotal}`;
    wrapper.appendChild(counter);

    // Navigation areas
    const navLeft = document.createElement('div');
    navLeft.className = 'clickreel-story-nav clickreel-story-nav-left';
    navLeft.innerHTML =
      '<div class="clickreel-story-chevron"><svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg></div>';
    navLeft.addEventListener('click', (e) => {
      e.stopPropagation();
      this.options.onStoryAdvance?.('prev');
    });
    wrapper.appendChild(navLeft);

    const navRight = document.createElement('div');
    navRight.className = 'clickreel-story-nav clickreel-story-nav-right';
    navRight.innerHTML =
      '<div class="clickreel-story-chevron"><svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg></div>';
    navRight.addEventListener('click', (e) => {
      e.stopPropagation();
      this.options.onStoryAdvance?.('next');
    });
    wrapper.appendChild(navRight);

    // Store refs and disable on mobile until video plays (so taps reach YouTube play button)
    this.storyNavLeft = navLeft;
    this.storyNavRight = navRight;
    if (isMobile()) {
      navLeft.style.pointerEvents = 'none';
      navRight.style.pointerEvents = 'none';
    }
  }

  private createSocialProof(wrapper: HTMLElement): void {
    if (!this.options.socialProof?.enabled) return;
    const badge = document.createElement('div');
    const pos = this.options.socialProof.position || 'top-left';
    badge.className = `clickreel-social-proof ${pos}`;
    badge.textContent = this.options.socialProof.text;
    wrapper.appendChild(badge);
  }

  private createVariantPicker(action: FloatingAction, actionBtn: HTMLAnchorElement): void {
    if (!action.variants || action.variants.length === 0) return;
    const picker = document.createElement('div');
    picker.className = 'clickreel-variant-picker';
    const maxShow = 6;
    const variants = action.variants.slice(0, maxShow);
    let selectedId: string | null = null;

    variants.forEach((v) => {
      if (v.type === 'color') {
        const swatch = document.createElement('div');
        swatch.className = 'clickreel-variant-swatch';
        if (v.available === false) swatch.classList.add('unavailable');
        swatch.style.background = v.colorHex || '#888';
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (v.available === false) return;
          picker
            .querySelectorAll('.clickreel-variant-swatch')
            .forEach((s) => s.classList.remove('selected'));
          swatch.classList.add('selected');
          selectedId = v.id;
          this.updateActionUrl(actionBtn, action, selectedId);
          this.updateActionLabel(actionBtn, action, v.label);
        });
        picker.appendChild(swatch);
      } else {
        const chip = document.createElement('button');
        chip.className = 'clickreel-variant-chip';
        if (v.available === false) chip.classList.add('unavailable');
        chip.textContent = v.label;
        chip.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (v.available === false) return;
          picker
            .querySelectorAll('.clickreel-variant-chip')
            .forEach((c) => c.classList.remove('selected'));
          chip.classList.add('selected');
          selectedId = v.id;
          this.updateActionUrl(actionBtn, action, selectedId);
          this.updateActionLabel(actionBtn, action, v.label);
        });
        picker.appendChild(chip);
      }
    });

    if (action.variants.length > maxShow) {
      const more = document.createElement('span');
      more.className = 'clickreel-variant-more';
      more.textContent = `+${action.variants.length - maxShow} more`;
      picker.appendChild(more);
    }

    // Insert after icon wrapper, before text container
    const textEl = actionBtn.querySelector('.clickreel-floating-action-text');
    if (textEl) actionBtn.insertBefore(picker, textEl);
    else actionBtn.appendChild(picker);
  }

  private updateActionUrl(btn: HTMLAnchorElement, action: FloatingAction, variantId: string): void {
    const param = action.selectedVariantParam || 'v';
    const url = new URL(action.url);
    url.searchParams.set(param, variantId);
    btn.href = url.toString();
  }

  private updateActionLabel(
    btn: HTMLAnchorElement,
    action: FloatingAction,
    variantLabel: string,
  ): void {
    const label = btn.querySelector('.clickreel-floating-action-label');
    if (label) label.textContent = `${action.label} — ${variantLabel}`;
  }

  private applyPiPStyles(): void {
    if (!this.container) return;

    const { position } = this.options;
    const margin = '24px';

    let positionStyle = '';
    switch (position) {
      case 'bottom-right':
        positionStyle = `bottom: ${margin}; right: ${margin};`;
        break;
      case 'bottom-left':
        positionStyle = `bottom: ${margin}; left: ${margin};`;
        break;
      case 'top-right':
        positionStyle = `top: ${margin}; right: ${margin};`;
        break;
      case 'top-left':
        positionStyle = `top: ${margin}; left: ${margin};`;
        break;
    }

    this.container.style.cssText = positionStyle;
  }

  // ===========================================================================
  // Private Methods - Progress Tracking
  // ===========================================================================

  private startProgressTracking(): void {
    this.stopProgressTracking();
    this.progressInterval = window.setInterval(() => {
      this.updateProgressUI();
      this.checkQuartiles();
    }, 500);
  }

  private stopProgressTracking(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private updateProgressUI(): void {
    if (!this.player || !this.container) return;

    const currentTime = this.player.getCurrentTime();
    const duration = this.player.getDuration();

    if (!duration || isNaN(duration)) return;

    const percent = (currentTime / duration) * 100;

    // Update progress bar
    const fill = this.container.querySelector('.clickreel-progress-fill') as HTMLElement;
    if (fill) {
      fill.style.width = `${percent}%`;
    }

    // Update time display
    const timeDisplay = this.container.querySelector('.clickreel-time-display');
    if (timeDisplay) {
      timeDisplay.textContent = `${this.formatTime(currentTime)} / ${this.formatTime(duration)}`;
    }

    // Update story progress fill bar
    if (this.options.storyMode) {
      const storyFill = this.container.querySelector(
        '.clickreel-story-segment.active .clickreel-story-fill',
      ) as HTMLElement;
      if (storyFill) {
        storyFill.style.width = `${percent}%`;
      }
    }

    // Trigger product card at configured percentage
    if (this.productCardInstance && !this.productCardInstance.hasShown) {
      if (percent >= this.productCardInstance.triggerPercent) {
        this.productCardInstance.show();
      }
    }
  }

  private checkQuartiles(): void {
    if (!this.player) return;

    const currentTime = this.player.getCurrentTime();
    const duration = this.player.getDuration();

    if (!duration || isNaN(duration)) return;

    const percent = Math.floor((currentTime / duration) * 100);

    // Emit quartile events (25%, 50%, 75%)
    const quartiles = [25, 50, 75];
    for (const q of quartiles) {
      if (percent >= q && this.lastQuartile < q) {
        this.emitProgress(q as 25 | 50 | 75);
        this.lastQuartile = q;
      }
    }
  }

  private emitProgress(quartile: 0 | 25 | 50 | 75 | 100): void {
    if (!this.player) return;

    const currentTime = this.player.getCurrentTime();
    const duration = this.player.getDuration();

    const data: VideoProgressData = {
      videoId: this.options.videoId,
      currentTime,
      duration,
      percent: Math.floor((currentTime / duration) * 100),
      quartile,
    };

    this.options.onProgress?.(data);
  }

  private updatePlayButton(isPlaying: boolean): void {
    const btn = this.container?.querySelector('.clickreel-play-btn');
    if (btn) {
      btn.innerHTML = isPlaying ? this.getPauseIcon() : this.getPlayIcon();
    }
  }

  private isPlayerMuted(): boolean {
    if (!this.player || !this.playerReady) return this.isMutedState;
    try {
      return typeof this.player.isMuted === 'function' ? this.player.isMuted() : this.isMutedState;
    } catch {
      return this.isMutedState;
    }
  }

  private updateMuteButton(isMuted: boolean): void {
    this.isMutedState = isMuted;
    const btn = this.container?.querySelector('.clickreel-mute-btn');
    if (btn) {
      btn.innerHTML = isMuted ? this.getMuteIcon() : this.getVolumeIcon();
    }
  }

  private handleEscapeKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  // ===========================================================================
  // Private Methods - Utilities
  // ===========================================================================

  private formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private getPlayIcon(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5.14v13.72c0 .89.97 1.44 1.71.97l10.49-6.86a1.16 1.16 0 000-1.94L9.71 4.17A1.16 1.16 0 008 5.14z"/>
    </svg>`;
  }

  private getPauseIcon(): string {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
    </svg>`;
  }

  private getVolumeIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
    </svg>`;
  }

  private getMuteIcon(): string {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
    </svg>`;
  }

  private injectStyles(shadow: ShadowRoot): void {
    injectUtilityStyles(shadow);

    const existingStyle = shadow.querySelector('style[data-youtube-player]');
    if (existingStyle) return;

    const style = document.createElement('style');
    style.setAttribute('data-youtube-player', 'true');
    style.textContent = `
      .clickreel-video-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.75);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: clickreel-fade-in 0.2s ease;
        pointer-events: auto;
        z-index: 2;
      }

      /* Mobile: Full black background for immersive Reels-like experience */
      @media (max-width: 767px) {
        .clickreel-video-overlay {
          background: #000;
        }
      }

      .clickreel-video-overlay.closing {
        animation: clickreel-fade-out 0.2s ease forwards;
      }

      @keyframes clickreel-fade-in {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes clickreel-fade-out {
        from { opacity: 1; }
        to { opacity: 0; }
      }

      @keyframes clickreel-slide-up {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      /* Desktop PiP Style */
      .clickreel-video-container.desktop {
        position: absolute;
        width: 380px;
        max-width: 90vw;
        background: #000;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        animation: clickreel-slide-up 0.3s ease;
      }

      /* Mobile Fullscreen - Instagram Reels style */
      .clickreel-video-container.mobile {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        height: 100dvh; /* Dynamic viewport height for mobile browsers */
        background: #000;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      .clickreel-close-btn {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 100;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.5);
        border: none;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s, opacity 0.2s;
        cursor: pointer;
        padding: 0;
      }

      .clickreel-close-btn:hover {
        background: rgba(0, 0, 0, 0.7);
      }

      /* Mobile: Larger touch target with contrast ring in safe area */
      .mobile .clickreel-close-btn {
        top: max(16px, env(safe-area-inset-top, 16px));
        right: max(16px, env(safe-area-inset-right, 16px));
        width: 44px;
        height: 44px;
        background: rgba(0, 0, 0, 0.6);
        border: 2px solid rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
      }

      .mobile .clickreel-close-btn:active {
        background: rgba(0, 0, 0, 0.8);
        border-color: rgba(255, 255, 255, 0.6);
      }

      .clickreel-video-wrapper {
        position: relative;
        width: 100%;
        background: #000;
      }

      .clickreel-video-wrapper[data-orientation="portrait"] {
        aspect-ratio: 9/16;
      }

      .clickreel-video-wrapper[data-orientation="landscape"] {
        aspect-ratio: 16/9;
      }

      .clickreel-video-wrapper[data-orientation="square"] {
        aspect-ratio: 1;
      }

      /* Mobile: Video fills entire screen */
      .mobile .clickreel-video-wrapper {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        aspect-ratio: unset;
      }

      .clickreel-youtube-container {
        position: relative;
        width: 100%;
        height: 100%;
        background: #000;
        z-index: 1;
      }

      .clickreel-youtube-container iframe {
        width: 100%;
        height: 100%;
        border: none;
      }

      /* Mobile: Ensure iframe fills screen properly */
      .mobile .clickreel-youtube-container {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
      }

      .mobile .clickreel-youtube-container iframe {
        width: 100%;
        height: 100%;
        max-height: 100vh;
        max-height: 100dvh;
        object-fit: contain;
      }

      .clickreel-video-controls {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 12px 16px;
        background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
        display: flex;
        align-items: center;
        gap: 12px;
        opacity: 0;
        transition: opacity 0.2s;
      }

      .clickreel-video-wrapper:hover .clickreel-video-controls,
      .clickreel-video-controls:focus-within {
        opacity: 1;
      }

      .mobile .clickreel-video-controls {
        opacity: 1;
      }

      .clickreel-yt-controls {
        position: relative;
        opacity: 1;
        background: rgba(0, 0, 0, 0.8);
      }

      .clickreel-play-btn,
      .clickreel-mute-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.1);
        border: none;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        cursor: pointer;
      }

      .clickreel-play-btn:hover,
      .clickreel-mute-btn:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      .clickreel-progress-container {
        flex: 1;
        cursor: pointer;
        padding: 8px 0;
      }

      .clickreel-progress-bar {
        height: 4px;
        background: rgba(255, 255, 255, 0.3);
        border-radius: 2px;
        overflow: hidden;
      }

      .clickreel-progress-fill {
        height: 100%;
        background: white;
        width: 0;
        transition: width 0.1s;
      }

      .clickreel-time-display {
        color: white;
        font-size: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        white-space: nowrap;
      }

      .clickreel-cta-btn {
        display: block;
        padding: 14px 24px;
        margin: 16px;
        border-radius: 8px;
        color: white;
        text-align: center;
        text-decoration: none;
        font-weight: 600;
        font-size: 16px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        transition: transform 0.2s, filter 0.2s;
      }

      .clickreel-cta-btn:hover {
        transform: translateY(-2px);
        filter: brightness(1.1);
      }

      .mobile .clickreel-cta-btn {
        margin: 0 16px 24px;
      }

      /* Floating Product Actions - Instagram/TikTok Shopping Style */
      @keyframes clickreel-float-in {
        0% {
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }
        100% {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      @keyframes clickreel-pulse-ring {
        0%, 100% {
          transform: scale(1);
          opacity: 0;
        }
        50% {
          transform: scale(1.15);
          opacity: 0.6;
        }
      }

      .clickreel-floating-actions {
        position: absolute;
        bottom: 24px;
        left: 16px;
        right: 16px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        z-index: 100;
        pointer-events: auto;
        transform: translateZ(0);
      }

      .mobile .clickreel-floating-actions {
        bottom: 24px;
        bottom: calc(env(safe-area-inset-bottom, 20px) + 24px);
        left: 12px;
        right: 12px;
        z-index: 100;
      }

      .clickreel-floating-action {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        background: rgba(0, 0, 0, 0.9);
        -webkit-backdrop-filter: blur(24px);
        backdrop-filter: blur(24px);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 16px;
        text-decoration: none;
        color: white;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        cursor: pointer;
        opacity: 1;
        visibility: visible;
        transform: translateZ(0);
        animation: clickreel-float-in 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.3s backwards;
      }

      .clickreel-floating-action:hover {
        transform: translateY(-3px) scale(1.02) translateZ(0);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3);
        border-color: rgba(255, 255, 255, 0.25);
      }

      .clickreel-floating-action:active {
        transform: translateY(-1px) scale(0.99) translateZ(0);
      }

      .clickreel-floating-action-icon-wrap {
        position: relative;
        flex-shrink: 0;
      }

      .clickreel-floating-action-icon-wrap::before {
        content: '';
        position: absolute;
        top: -4px;
        right: -4px;
        bottom: -4px;
        left: -4px;
        border-radius: 14px;
        background: linear-gradient(135deg, #ff6b6b, #feca57, #48dbfb);
        opacity: 0;
        animation: clickreel-pulse-ring 2s ease-in-out infinite;
        z-index: -1;
      }

      .clickreel-floating-action:hover .clickreel-floating-action-icon-wrap::before {
        opacity: 1;
      }

      .clickreel-floating-action-img {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        object-fit: cover;
        border: 2px solid rgba(255, 255, 255, 0.2);
      }

      .clickreel-floating-action-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
      }

      .clickreel-floating-action-text {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-width: 0;
        gap: 2px;
      }

      .clickreel-floating-action-label {
        font-weight: 600;
        font-size: 14px;
        line-height: 1.3;
        letter-spacing: -0.01em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: #fff;
      }

      .clickreel-floating-action-subtitle {
        font-size: 13px;
        font-weight: 500;
        color: #4ade80;
        letter-spacing: -0.01em;
      }

      .clickreel-floating-action-btn {
        flex-shrink: 0;
        padding: 8px 16px;
        background: linear-gradient(135deg, #fff 0%, #f0f0f0 100%);
        color: #1a1a1a;
        font-size: 13px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        transition: all 0.2s ease;
      }

      .clickreel-floating-action:hover .clickreel-floating-action-btn {
        background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
      }

      /* Desktop adjustments */
      .desktop .clickreel-floating-actions {
        max-width: 340px;
        left: 50%;
        transform: translateX(-50%) translateZ(0);
        bottom: 20px;
      }

      .desktop .clickreel-floating-action {
        animation-delay: 0.5s;
      }

      .clickreel-player-watermark {
        position: absolute;
        bottom: 6px;
        left: 0;
        right: 0;
        text-align: center;
        font-size: 10px;
        color: rgba(255, 255, 255, 0.5);
        text-decoration: none;
        pointer-events: auto;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        transition: color 0.2s ease;
        z-index: 10;
      }
      .clickreel-player-watermark:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    `;
    shadow.appendChild(style);
  }
}
